/**
 * @file ChatLauncher.tsx
 * @description Круглая кнопка «Сообщения» справа внизу и подсказка над ней
 * (chat-window-design.md §1а, §6, §7; SC64).
 *
 * Кнопка 56, цвет действия, счётчик непрочитанных диалогов («99+»). Видна
 * только при праве «Переписка с клиентами» (провайдер выключен — ничего не
 * рисуем). Пока окно открыто — кнопки и подсказок нет. В редакторе страницы
 * сайта угол занят панелью настроек — там кнопка значком в верхней панели
 * (`variant="header"`, §9 вопрос 2).
 */

import { MessageSquare } from "lucide-react";
import type { CSSProperties } from "react";

import { cn } from "@/lib/utils";
import { CHAT_TEXT } from "./chat-texts";
import { ChatHint } from "./ChatHint";
import { launcherCount } from "./launcher-view";
import { useChatWindow } from "./chat-window-context";
import { FAB_SIZE, useBottomObstacle } from "./useBottomObstacle";

interface Props {
  variant?: "fab" | "header";
}

function CountBadge({ count, className }: { count: string; className: string }) {
  return (
    <span
      aria-hidden
      className={cn(
        "absolute inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-destructive px-1",
        "text-xs font-semibold tabular-nums text-on-primary ring-2 ring-surface-lowest",
        className,
      )}
    >
      {count}
    </span>
  );
}

export function ChatLauncher({ variant = "fab" }: Props) {
  const chat = useChatWindow();
  const wide = chat.viewport === "desktop" || chat.viewport === "wide";
  const fabVisible = chat.enabled && variant === "fab" && !chat.state.open;
  const obstacle = useBottomObstacle(fabVisible, wide);
  // Число и подсказка — из провайдера окна (опрос `/admin/chat/summary` там).
  const { unread, hint, dismissHint } = chat;

  if (!chat.enabled) return null;
  const count = launcherCount(unread);
  const label = CHAT_TEXT.launcherAria(unread);

  if (variant === "header") {
    return (
      <button
        ref={chat.launcherRef}
        type="button"
        aria-label={label}
        aria-expanded={chat.state.open}
        title={CHAT_TEXT.launcher(unread)}
        onClick={() => (chat.state.open ? chat.close() : chat.openChat())}
        className="relative inline-flex size-12 items-center justify-center rounded-lg text-on-surface outline-none hover:bg-surface-low focus-visible:ring-2 focus-visible:ring-ring lg:size-11"
      >
        <MessageSquare className="size-5" aria-hidden />
        {count ? <CountBadge count={count} className="-right-0.5 -top-0.5" /> : null}
      </button>
    );
  }

  if (!fabVisible || obstacle.hidden) return null;
  // До 1024 без полосы — плюс безопасная зона; полоса уже учла её своей высотой.
  const bottomCss = !wide && !obstacle.raised ? `calc(${obstacle.bottom}px + env(safe-area-inset-bottom))` : `${obstacle.bottom}px`;
  const style: CSSProperties = { bottom: bottomCss };

  return (
    <>
      {hint ? (
        <ChatHint
          hint={hint}
          bottom={obstacle.bottom + FAB_SIZE + 8}
          onOpen={(id) => {
            dismissHint();
            chat.openChat({ conversationId: id });
          }}
          onDismiss={dismissHint}
        />
      ) : null}
      <button
        ref={chat.launcherRef}
        type="button"
        aria-label={label}
        aria-expanded={false}
        title={CHAT_TEXT.launcher(unread)}
        onClick={() => chat.openChat()}
        style={style}
        className={cn(
          "fixed right-4 z-[var(--z-chat)] inline-flex size-14 items-center justify-center rounded-full lg:right-6",
          "bg-primary text-on-primary outline-none transition-transform active:scale-95",
          "[box-shadow:0_8px_24px_var(--shadow-ambient)] focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
        )}
      >
        <MessageSquare className="size-6" aria-hidden />
        {count ? <CountBadge count={count} className="-right-1 -top-1" /> : null}
      </button>
    </>
  );
}
